import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState } from 'react';

import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { PhoneNumberTextField } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { isValidPhone10 } from '_lib/utils/helpers';

type DnisEditCardProps = {
  initialPhoneNumber?: string | null;
  initialGreetingOverride?: string | null;
  initialAiGreetingOverride?: string | null;
  isActive?: boolean;
  onCancel: () => void;
  onSave: (dnis: { phoneNumber: string; greetingOverride: string | null; aiGreetingOverride: string | null }) => void;
};

export const DnisEditCard = (props: DnisEditCardProps) => {
  const {
    initialPhoneNumber,
    initialGreetingOverride,
    initialAiGreetingOverride,
    isActive = true,
    onCancel,
    onSave
  } = props;

  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '');
  const [greetingOverride, setGreetingOverride] = useState(initialGreetingOverride || '');
  const [aiGreetingOverride, setAiGreetingOverride] = useState(initialAiGreetingOverride || '');
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  const isPhoneValid = isValidPhone10(phoneNumber);
  const isAiGreetingRequiredButMissing = !!(greetingOverride.trim() && !aiGreetingOverride.trim());

  const handleSave = () => {
    setSaveButtonClicked(true);
    if (!phoneNumber || !isPhoneValid || isAiGreetingRequiredButMissing) {
      return;
    }
    onSave({
      phoneNumber,
      greetingOverride: greetingOverride.trim() || null,
      aiGreetingOverride: aiGreetingOverride.trim() || null
    });
  };

  return (
    <FlexCard>
      <Stack useFlexGap sx={{ padding: 2, width: '100%' }} spacing={2}>
        <PhoneNumberTextField
          label='DNIS Number'
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={!isActive}
          error={saveButtonClicked && !isPhoneValid}
          helperText={saveButtonClicked && !isPhoneValid ? 'Please enter a valid 10-digit phone number' : ''}
          required
          fullWidth
        />
        <TextField
          label='Greeting Override'
          value={greetingOverride}
          onChange={(e) => setGreetingOverride(e.target.value)}
          disabled={!isActive}
          multiline
          maxRows={10}
          fullWidth
        />
        <TextField
          label='CPI Greeting Override'
          value={aiGreetingOverride}
          onChange={(e) => setAiGreetingOverride(e.target.value)}
          disabled={!isActive}
          error={saveButtonClicked && isAiGreetingRequiredButMissing}
          helperText={
            saveButtonClicked && isAiGreetingRequiredButMissing
              ? 'AI greeting override is required when greeting override is set'
              : ''
          }
          multiline
          maxRows={10}
          fullWidth
        />
      </Stack>
      <Divider />
      <FlexBox sx={{ justifyContent: 'flex-end', gap: 1, marginY: 1, marginRight: 1 }}>
        <Button
          text='Cancel'
          variant='contained'
          color='error'
          size='small'
          sx={{ textTransform: 'none' }}
          startIcon={<CancelIcon />}
          onClick={onCancel}
        />
        <Button
          text='Save'
          variant='contained'
          size='small'
          sx={{ textTransform: 'none' }}
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!phoneNumber || !isActive}
        />
      </FlexBox>
    </FlexCard>
  );
};
